import React, { useState, useEffect } from "react";
import { FileText, Download, AlertCircle } from "lucide-react";
import { TOKENS } from "../tokens";
import { Card } from "./Card";
import { SectionLabel } from "./SectionLabel";
import api from "../api/client";

export function MonthlyPerformanceReports({ employeeId, isManagerView = false }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    // Default to last month if today is 1st or 2nd of month, otherwise current month
    if (d.getDate() <= 5) {
      d.setMonth(d.getMonth() - 1);
    }
    return d.toISOString().substring(0, 7);
  });

  const monthsList = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthsList.push(d.toISOString().substring(0, 7));
  }

  async function fetchReports() {
    try {
      setLoading(true);
      const url = isManagerView && employeeId 
        ? `/reports/monthly-performance?employeeId=${employeeId}`
        : `/reports/monthly-performance`;
      
      const res = await api.get(url);
      setReports(res.data || []);
    } catch (err) {
      console.error("Failed to fetch monthly reports:", err);
      setError("Could not load monthly performance reports.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReports();
  }, [employeeId, isManagerView]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError(null);
      await api.post("/reports/generate-monthly", {
        employeeId: isManagerView ? employeeId : undefined,
        monthStr: selectedMonth
      });
      await fetchReports();
    } catch (err) {
      console.error("Failed to generate report:", err);
      setError("Failed to generate the monthly report.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <>
        <SectionLabel>Monthly Performance Reports</SectionLabel>
        <Card style={{ padding: 16, textAlign: "center", color: TOKENS.muted }}>
          <span style={{ animation: "spin 1s linear infinite", display: "inline-block", marginRight: 8 }}>⟳</span>
          Loading reports...
        </Card>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SectionLabel>Monthly Performance Reports</SectionLabel>
        <Card style={{ padding: 16, display: "flex", gap: 10, alignItems: "center", color: TOKENS.danger, background: TOKENS.dangerBg }}>
          <AlertCircle size={20} /> {error}
        </Card>
      </>
    );
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <SectionLabel>Monthly Performance Reports</SectionLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              padding: "5px 10px", borderRadius: 8, border: `1px solid ${TOKENS.border}`,
              fontSize: 12, fontWeight: 600, background: "#fff", color: TOKENS.navyDeep
            }}
          >
            {monthsList.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <button 
            onClick={handleGenerate}
            disabled={generating}
            style={{ background: TOKENS.navyDeep, color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: generating ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 4 }}
          >
            <FileText size={12} /> {generating ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>
      <Card style={{ padding: 14 }}>
        {reports.length === 0 ? (
          <div style={{ textAlign: "center", padding: 16, color: TOKENS.muted, fontSize: 12.5 }}>
            <FileText size={28} color={TOKENS.border} style={{ margin: "0 auto 6px", display: "block" }} />
            No monthly performance reports available yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {reports.map((rep, idx) => (
              <div key={idx} style={{
                background: TOKENS.cream, borderRadius: 12, padding: 12,
                border: `1.5px solid ${TOKENS.border}`, display: "flex",
                alignItems: "center", justifyContent: "space-between", gap: 10
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 10, background: `${TOKENS.gold}15`,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                  }}>
                    <FileText size={22} color={TOKENS.gold} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.navyDeep, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      Performance - {rep.month}
                    </div>
                    <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2 }}>
                      PDF Report
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <a
                    href={`${api.defaults.baseURL.replace('/api', '')}${rep.url.startsWith('/api') ? '' : '/api'}${rep.url}?token=${localStorage.getItem("fp_token")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    style={{
                      background: TOKENS.navyDeep, color: "#fff", border: "none",
                      borderRadius: 8, padding: "7px 12px", fontSize: 11.5, fontWeight: 700,
                      textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5,
                      cursor: "pointer"
                    }}
                  >
                    <Download size={13} /> View PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
